import { useEffect, useMemo } from 'react';
import { useVehicleApi, useVehicleCreateApi, useVehicleUpdateApi } from '@hero/api-hooks';
import { getObjectId, Vehicle } from '@hero/schema';
import { VehicleFormValues, useVehicleForm, UseVehicleFormOptions } from './useVehicleForm';

export interface UseVehicleCreateManagerOptions {
  /** Called after create or update. */
  onVehicleSaved?: (vehicle: Vehicle) => void;
  /** When set, load vehicle for edit; when undefined, create mode. */
  vehicleId?: Parameters<typeof useVehicleApi>[0];
  /** Optional initial form values (e.g. pre-fill from context). */
  defaultValues?: UseVehicleFormOptions['defaultValues'];
}

export const useVehicleCreateManager = (options: UseVehicleCreateManagerOptions) => {
  const { vehicleId, onVehicleSaved } = options;
  const isEdit = vehicleId != null;
  const vehicleQuery = useVehicleApi(vehicleId);
  const vehicleCreate = useVehicleCreateApi();
  const vehicleUpdate = useVehicleUpdateApi();
  const savedVehicle = vehicleQuery.data;

  const defaultValues = useMemo((): Partial<VehicleFormValues> => {
    return {
      country: 'saudi_arabia',
    };
  }, []);

  async function onSubmit(values: VehicleFormValues) {
    try {
      if (savedVehicle) {
        const result = await vehicleUpdate.mutateAsync({
          _id: getObjectId(savedVehicle._id),
          ...values,
        });
        onVehicleSaved?.(result);
      } else {
        const result = await vehicleCreate.mutateAsync(values);
        onVehicleSaved?.(result);
      }
    } catch (error) {
      console.error('Failed to save vehicle:', error);
    }
  }

  const formHook = useVehicleForm({
    defaultValues: options.defaultValues ?? defaultValues,
    onSubmit,
  });

  useEffect(() => {
    if (vehicleId != null && savedVehicle) {
      formHook.setVehicle(savedVehicle);
    }
  }, [vehicleId, savedVehicle, formHook.setVehicle]);

  const reset = () => {
    vehicleCreate.reset();
    vehicleUpdate.reset();
    if (savedVehicle) {
      formHook.setVehicle(savedVehicle);
    } else {
      formHook.reset(defaultValues);
    }
  };

  const onSaveClick = () => {
    formHook.form.handleSubmit(formHook.onValidSubmit)();
  };

  return {
    vehicleQuery,
    vehicleCreate,
    vehicleUpdate,
    formHook,
    reset,
    onSaveClick,
    savedVehicle,
  };
};
